/**
 * Implement of a line chart which displays the graphs of one series of points.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2025
 */

import { LineChart as MuiLineChart } from '@mui/x-charts/LineChart'

const DEFAULT_X_STEP = 1
const DEFAULT_HEIGHT = 500
const DEFAULT_COLOR = '#faa091'
const SERIES_OPACITY = 0.4

export interface LineChartProps {
  // List of x-values. Must have the same length as yValueList.
  xValueList: number[]
  // Minimum of the x-axis
  xMin?: number
  // Maximum of x-axis
  xMax?: number
  // Step of x-values
  xStep?: number
  // Label of the x-axis
  xAxisLabel?: string
  // Unit of the x-axis
  xUnit?: string

  // List of y-values. Must have the same length as xValueList.
  yValueList: number[]
  // Minimum of the y-axis
  yMin?: number
  // Maximum of the y-axis
  yMax?: number
  // Label of the y-axis
  yAxisLabel?: string
  // Label of the y-values in the tooltip
  yValueLabel?: string
  // Unit of the y-axis
  yUnit?: string

  // Width of the chart
  width?: number
  // Height of the chart
  height?: number
  // Color of the chart
  color?: string
  skipAnimation?: boolean
}

/**
 * Generate a list containing step values.
 *
 * @param step The step.
 * @param max The max value.
 * @returns The list of steps.
 */
function generateStepList(step: number, min: number, max: number): number[] {
  const length = Math.floor((max - min) / step) + 1
  return Array.from({ length }, (_, i) =>
    parseFloat(((i + min) * step).toFixed(10))
  )
}

const LineChart = (props: LineChartProps) => {
  const xStep = props.xStep ?? DEFAULT_X_STEP
  const firstX = props.xValueList.at(0) ?? 0
  const lastX = props.xValueList.at(-1) ?? 0
  const chartWidth = props.width
  const chartHeight = props.height ?? DEFAULT_HEIGHT

  // Get the smallest value that is a multiple of xStep and greater than or
  // equal to the last X-value.
  const xMax = props.xMax ?? Math.ceil(lastX / xStep) * xStep
  const xMin = props.xMin ?? Math.floor(firstX / xStep) * xStep

  const xTickList = generateStepList(xStep, xMin, xMax)

  const sparseYList = new Map(
    props.xValueList.map((x, i) => [x, props.yValueList[i]])
  )
  const completeYList = xTickList.map((x) => sparseYList.get(x) ?? null)

  return (
    <MuiLineChart
      margin={{ left: 70 }}
      xAxis={[
        {
          scaleType: 'linear',
          data: xTickList,
          min: props.xMin,
          label: `${props.xAxisLabel}${props.xUnit ? ` (${props.xUnit})` : ''}`,
          valueFormatter: (value, context) =>
            context.location === 'tooltip'
              ? `Time: ${value}${props.xUnit ? `${props.xUnit}` : ''}`
              : `${value}`,
        },
      ]}
      yAxis={[
        {
          min: props.yMin,
          max: props.yMax,
          label: `${props.yAxisLabel} ${props.yUnit ? ` (${props.yUnit})` : ''}`,
        },
      ]}
      series={[
        {
          type: 'line',
          data: completeYList,
          connectNulls: false,
          area: true,
          label: `${props.yValueLabel}`,
          color: props.color ?? DEFAULT_COLOR,
        },
      ]}
      sx={{
        '& .MuiAreaElement-root': {
          // Opacity of the area color, under the line.
          fillOpacity: SERIES_OPACITY,
        },
      }}
      width={chartWidth}
      height={chartHeight}
      grid={{ vertical: true, horizontal: true }}
      skipAnimation={props.skipAnimation}
    />
  )
}

export default LineChart
